import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { formatCurrency, formatNumberInput, parseItalianNumber } from '../../../utils/formatters';
import { formatDataRoma, giornoRoma } from '../../../utils/dateRoma';
import { API_DOCS } from './BuoniEmessiPage.api';
import {
  StatoBuono, TipoBuono, ESITI_AMMESSI, ETICHETTA_STATO_BUONO, BADGE_STATO_BUONO, ETICHETTA_TIPO_BUONO,
} from './percorsoTipi';

interface BuonoElenco {
  id: number; codice: string; tipo: TipoBuono; stato: StatoBuono; emessoAt: string; scadenzaAt: string;
  dataContratto: string | null; importoContratto: string | null; noteEsito: string | null;
  acquirente: { id: number; nome: string; cognome: string; unitaCodice: string; telefono: string | null };
  commessa: { id: number; numero: string; sede: { nome: string }; offertaCapitolato: { oggetto: string } | null };
  venditore: { id: number; nome: string };
}

const MOD = 'commerciale.percorso_buoni';
// La riga apre la scheda dell'acquirente, che richiede uno di questi due permessi (vedi la rotta in App.tsx).
const MOD_SCHEDA = ['commerciale.percorso_agenda', 'commerciale.commesse'];
const ATTESA_RICERCA_MS = 300;

// Da usare solo sul testo digitato dall'utente, non sul valore che arriva dall'API.
// In questo campo, punto + tre cifre ("9.800") sono migliaia, non 9,8 euro.
const leggiImporto = (testo: string): number => {
  const t = testo.trim();
  return parseItalianNumber(/^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t);
};

function ModaleEsito({ buono, onClose, onSalvato }: { buono: BuonoElenco; onClose: () => void; onSalvato: () => void }) {
  const opzioni = ESITI_AMMESSI[buono.stato];
  // Valori con cui si apre la modale: servono a sapere se ci sono modifiche non salvate.
  const [iniziali] = useState(() => ({
    stato: opzioni[0] ?? buono.stato,
    dataContratto: buono.dataContratto ? giornoRoma(buono.dataContratto) : '',
    importo: formatNumberInput(buono.importoContratto),
    note: buono.noteEsito ?? '',
  }));
  const [stato, setStato] = useState<StatoBuono>(iniziali.stato);
  const [dataContratto, setDataContratto] = useState(iniziali.dataContratto);
  const [importo, setImporto] = useState(iniziali.importo);
  const [note, setNote] = useState(iniziali.note);
  const [salvando, setSalvando] = useState(false);

  const isDirty = JSON.stringify({ stato, dataContratto, importo, note }) !== JSON.stringify(iniziali);
  useDirtyForm(isDirty, `Esito buono ${buono.codice}`);

  const salva = async () => {
    const corpo: { stato: StatoBuono; noteEsito: string; dataContratto?: string; importoContratto?: number } = { stato, noteEsito: note };
    if (stato === 'CONTRATTO_FIRMATO') {
      const importoContratto = leggiImporto(importo);
      if (!dataContratto) {
        showToast('Inserisci la data del contratto', 'error');
        return;
      }
      if (!Number.isFinite(importoContratto) || importoContratto <= 0) {
        showToast('Inserisci un importo del contratto maggiore di zero', 'error');
        return;
      }
      corpo.dataContratto = dataContratto;
      corpo.importoContratto = importoContratto;
    }
    setSalvando(true);
    try {
      // Data e importo si spediscono solo con CONTRATTO_FIRMATO: per gli altri stati il server li azzera.
      // @api PUT /api/percorso-acquirenti/buoni/:id/esito {"stato":"string","dataContratto":"string","importoContratto":"number","noteEsito":"string"} -> {"success":true}
      await apiFetch(`/api/percorso-acquirenti/buoni/${buono.id}/esito`, { method: 'PUT', body: JSON.stringify(corpo) });
      showToast(`Esito di ${buono.codice} aggiornato`);
      onSalvato();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    // Niente chiusura al clic sullo sfondo: un clic fuori posto farebbe perdere quanto digitato.
    <div className="bop-modal-overlay">
      <div className="bop-modal">
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Esito del buono {buono.codice}</h2>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">✕</button>
        </div>
        <div className="bop-modal__body bop-percorso__sezione">
          <p className="bop-text-muted">{ETICHETTA_TIPO_BUONO[buono.tipo]} · {buono.acquirente.cognome} {buono.acquirente.nome} · stato attuale: {ETICHETTA_STATO_BUONO[buono.stato]}</p>
          {opzioni.length === 0 ? (
            <p className="bop-text-muted">Il buono è annullato: nessun esito modificabile.</p>
          ) : (
            <>
              <div className="bop-form-group">
                <label className="bop-label">Stato</label>
                <select className="bop-select" value={stato} onChange={e => setStato(e.target.value as StatoBuono)}>
                  {opzioni.map(o => <option key={o} value={o}>{ETICHETTA_STATO_BUONO[o]}</option>)}
                </select>
              </div>
              {stato === 'CONTRATTO_FIRMATO' && (
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Data del contratto</label>
                    <input className="bop-input" type="date" value={dataContratto} onChange={e => setDataContratto(e.target.value)} />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Importo del contratto (€)</label>
                    {/* All'uscita dal campo l'importo si riscrive in formato italiano, così si vede come è stato letto. */}
                    <input className="bop-input" inputMode="decimal" value={importo}
                      onChange={e => setImporto(e.target.value)}
                      onBlur={() => setImporto(i => (i.trim() === '' ? i : formatNumberInput(leggiImporto(i)) || i))} />
                  </div>
                </div>
              )}
              {buono.stato === 'CONTRATTO_FIRMATO' && stato !== 'CONTRATTO_FIRMATO' && (
                <p className="bop-text-muted">Data e importo del contratto verranno cancellati.</p>
              )}
              {stato === 'ANNULLATO' && buono.stato !== 'ANNULLATO' && (
                <p className="bop-text-muted">L'annullamento è definitivo.</p>
              )}
              <div className="bop-form-group">
                <label className="bop-label">Note (es. chi l'ha comunicato)</label>
                <textarea className="bop-input bop-percorso__textarea" value={note} onChange={e => setNote(e.target.value)} />
              </div>
            </>
          )}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          {opzioni.length > 0 && <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>Salva</button>}
        </div>
      </div>
    </div>
  );
}

export default function BuoniEmessiPage() {
  const navigate = useNavigate();
  const { societaId, pronta } = useFiltroSocieta();
  const { can } = usePermesso();
  const puoAprireScheda = MOD_SCHEDA.some(m => can(m, 'view'));
  const [stato, setStato] = useState('');
  const [tipo, setTipo] = useState('');
  // q è il testo nel campo; qCerca è quello effettivamente cercato, 300 ms dopo l'ultimo tasto.
  const [q, setQ] = useState('');
  const [qCerca, setQCerca] = useState('');
  const [buoni, setBuoni] = useState<BuonoElenco[] | null>(null);
  const [errore, setErrore] = useState('');
  const [inModifica, setInModifica] = useState<BuonoElenco | null>(null);
  const richiestaCorrente = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setQCerca(q.trim()), ATTESA_RICERCA_MS);
    return () => clearTimeout(t);
  }, [q]);

  const carica = useCallback(() => {
    if (!pronta) return;
    // Risposte fuori ordine (filtri cambiati mentre la richiesta è in corso): vale solo l'ultima.
    const richiesta = ++richiestaCorrente.current;
    const qs = new URLSearchParams();
    if (societaId) qs.set('societaId', societaId);
    if (stato) qs.set('stato', stato);
    if (tipo) qs.set('tipo', tipo);
    if (qCerca) qs.set('q', qCerca);
    setErrore('');
    // @api GET /api/percorso-acquirenti/buoni?societaId&stato&tipo&q -> {"success":true,"data":[{"id":"number","codice":"string","tipo":"string","stato":"string","emessoAt":"string","scadenzaAt":"string","dataContratto":"string","importoContratto":"string","noteEsito":"string","acquirente":"object","commessa":"object","venditore":"object"}]}
    apiFetch<{ success: boolean; data: BuonoElenco[] }>(`/api/percorso-acquirenti/buoni?${qs}`)
      .then(r => { if (richiesta === richiestaCorrente.current) setBuoni(r.data); })
      .catch(e => { if (richiesta === richiestaCorrente.current) setErrore(e.message); });
  }, [pronta, societaId, stato, tipo, qCerca]);

  useEffect(() => { carica(); }, [carica]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Buoni emessi</h1>
          <p className="bop-page-subtitle">Buoni Atrio e Aran consegnati agli acquirenti; l'esito si aggiorna quando Atrio o Aran lo comunicano</p>
        </div>
      </div>

      <div className="bop-filter-bar">
        <input className="bop-input" placeholder="Cerca codice o cognome…" value={q} onChange={e => setQ(e.target.value)} />
        <select className="bop-select" value={tipo} onChange={e => setTipo(e.target.value)} aria-label="Tipo">
          <option value="">Tutti i tipi</option>
          {(Object.keys(ETICHETTA_TIPO_BUONO) as TipoBuono[]).map(t => <option key={t} value={t}>{ETICHETTA_TIPO_BUONO[t]}</option>)}
        </select>
        <select className="bop-select" value={stato} onChange={e => setStato(e.target.value)} aria-label="Stato">
          <option value="">Tutti gli stati</option>
          {(Object.keys(ETICHETTA_STATO_BUONO) as StatoBuono[]).map(s => <option key={s} value={s}>{ETICHETTA_STATO_BUONO[s]}</option>)}
        </select>
      </div>

      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

      <div className="bop-card">
        <table className={`bop-table${puoAprireScheda ? ' bop-table--clickable' : ''}`}>
          <thead>
            <tr>
              <th>Codice</th><th>Tipo</th><th>Acquirente</th><th>Cantiere</th><th>Venditore</th><th>Emesso</th><th>Scadenza</th><th>Stato</th><th className="bop-text-right">Contratto</th><th className="bop-table__th--actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {!buoni || buoni.length === 0 ? (
              // Con un errore e nessun dato non c'è un esito da dichiarare ("Nessun buono" sarebbe falso): basta l'avviso sopra.
              (buoni || !errore) && <tr><td colSpan={10}><div className="bop-empty-state">{buoni ? 'Nessun buono' : 'Caricamento…'}</div></td></tr>
            ) : buoni.map(b => (
              <tr key={b.id} onClick={puoAprireScheda ? () => navigate(`/commerciale/percorso/acquirenti/${b.acquirente.id}`) : undefined}>
                <td><strong>{b.codice}</strong></td>
                <td>{ETICHETTA_TIPO_BUONO[b.tipo]}</td>
                <td>{b.acquirente.cognome} {b.acquirente.nome}<br /><span className="bop-text-muted">Unità {b.acquirente.unitaCodice}{b.acquirente.telefono ? ` · ${b.acquirente.telefono}` : ''}</span></td>
                <td>{b.commessa.offertaCapitolato?.oggetto || b.commessa.numero}<br /><span className="bop-text-muted">{b.commessa.sede.nome}</span></td>
                <td>{b.venditore.nome}</td>
                <td>{formatDataRoma(b.emessoAt)}</td>
                <td>{formatDataRoma(b.scadenzaAt)}</td>
                <td><span className={`bop-badge ${BADGE_STATO_BUONO[b.stato]}`}>{ETICHETTA_STATO_BUONO[b.stato]}</span></td>
                <td className="bop-text-right">{b.importoContratto ? formatCurrency(Number(b.importoContratto)) : '—'}</td>
                <td className="bop-table__td--actions">
                  <BopRowActions actions={[
                    { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => setInModifica(b), hidden: !can(MOD, 'edit') || ESITI_AMMESSI[b.stato].length === 0 },
                  ]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {inModifica && (
        <ModaleEsito key={inModifica.id} buono={inModifica} onClose={() => setInModifica(null)} onSalvato={() => { setInModifica(cur => (cur?.id === inModifica.id ? null : cur)); carica(); }} />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
